import './EasyDynamicEight.scss'

import React, { CSSProperties, FunctionComponent } from 'react'

export type DynamicEightColor = 'yellow' | 'blue' | 'lightyellow' | 'lightblue'
export type DynamicEightPosition = 'left' | 'right' | 'center'
export type DynamicEightSize = 'xs' | 's' | 'm' | 'l' | 'xl'
export type DynamicEightFit = 'width' | 'height'

export interface EasyDynamicEightType {
  dynamicEightEnabled?: boolean
  dynamicEightColor?: DynamicEightColor
  dynamicEightPosition?: DynamicEightPosition
  dynamicEightSize?: DynamicEightSize
  dynamicEightFit?: DynamicEightFit
  dynamicEightWidth?: string
  dynamicEightHeight?: string
  className?: string
}

const dynamicEightColorMap: Record<DynamicEightColor, string> = {
  yellow: `var(--Palette-Brand-Primary-Default, #FFB700)`,
  blue: `var(--Palette-Brand-Secondary-Base, #001187)`,
  lightyellow: `var(--Palette-Brand-Primary-100, #FFF1CC)`,
  lightblue: `var(--Palette-Brand-Tertiary-Base, #EBEDFF)`,
}

const isDynamicEightPosition = (
  value?: string
): value is DynamicEightPosition =>
  value === `left` || value === `right` || value === `center`

const isDynamicEightSize = (value?: string): value is DynamicEightSize =>
  value === `xs` ||
  value === `s` ||
  value === `m` ||
  value === `l` ||
  value === `xl`

const isDynamicEightColor = (value?: string): value is DynamicEightColor =>
  value === `yellow` ||
  value === `blue` ||
  value === `lightyellow` ||
  value === `lightblue`

const isDynamicEightFit = (value?: string): value is DynamicEightFit =>
  value === `width` || value === `height`

const EasyDynamicEight: FunctionComponent<EasyDynamicEightType> = (props) => {
  if (props.dynamicEightEnabled === false) {
    return null
  }

  if (
    !props.dynamicEightColor ||
    !props.dynamicEightPosition ||
    !props.dynamicEightSize
  ) {
    return null
  }

  const position = isDynamicEightPosition(props.dynamicEightPosition)
    ? props.dynamicEightPosition
    : `center`
  const size = isDynamicEightSize(props.dynamicEightSize)
    ? props.dynamicEightSize
    : `m`
  const color = isDynamicEightColor(props.dynamicEightColor)
    ? dynamicEightColorMap[props.dynamicEightColor]
    : dynamicEightColorMap.blue
  const fit = isDynamicEightFit(props.dynamicEightFit)
    ? props.dynamicEightFit
    : `width`
  const style: CSSProperties & {
    '--EasyDynamicEight-width'?: string
    '--EasyDynamicEight-height'?: string
  } = {
    color,
    '--EasyDynamicEight-width': props.dynamicEightWidth,
    '--EasyDynamicEight-height': props.dynamicEightHeight,
  }

  return (
    <div
      className={[
        `EasyDynamicEight`,
        `EasyDynamicEight--${position}`,
        `EasyDynamicEight--${size}`,
        `EasyDynamicEight--fit${fit === `height` ? `Height` : `Width`}`,
        props.className,
      ]
        .filter(Boolean)
        .join(` `)}
      style={style}
      aria-hidden='true'
    />
  )
}

export default EasyDynamicEight
